import { useState } from 'react';
import { useAuth } from '../../Contexts/AuthProvider';
import LibModal from '../../features/LibModal/LibModal';
import { useTemplate } from '../../features/template/TemplateProvider';
import PageTitle from '../../utils/PageTitle';
import { EditIcon, LockFillIcon, ShieldFillIcon } from '../../utils/SVGIcon';
import cn from '../../utils/cn';
import { handleDisable2faOpe } from './SecurityOperation';
import ChangePasswordModal from './modals/ChangePasswordModal';
import TwoFacAuthModal from './modals/TwoFacAuthModal';

function ToggleMenu({ onClick, tfaEnabled }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn('bg-primary-100 rounded-full w-[27px] h-[16px] relative', {
                'bg-secondary': tfaEnabled
            })}
        >
            <span
                className={cn(
                    'inline-block h-[9px] w-[9px] rounded-full bg-white absolute top-1/2 transform -translate-y-1/2 ',
                    {
                        'left-[3px]': !tfaEnabled,
                        'right-[3px]': tfaEnabled
                    }
                )}
            />
        </button>
    );
}

function InfoUpdateRow({ icon, title, subTitle, onClick, action }) {
    return (
        <div className="flex items-center gap-x-5 py-3 border-b border-primary-100">
            <div className="w-[45px] h-[45px] bg-primary-100 p-2 rounded-lg">{icon}</div>
            <div className="flex-1 text-base font-medium leading-normal text-primary">
                <h2 className="text-primary font-normal opacity-60">{title}</h2>
                <p className=" text-primary">{subTitle}</p>
            </div>

            <div>
                {action || (
                    <button
                        type="button"
                        onClick={onClick}
                        className="w-7.5 h-7.5 flex justify-center items-center p-2 bg-primary-100 rounded-full"
                    >
                        <EditIcon />
                    </button>
                )}
            </div>
        </div>
    );
}

function SecurityPage() {
    // --Modal open handler
    const [modalType, setModalType] = useState('');

    // set page title
    PageTitle(`Security`);

    const { authInfo, authStateUpdated } = useAuth();
    const { tfaEnabled } = authInfo?.data || {};

    const { setMessage, setConfirm } = useTemplate();

    const handleToggleMenu = () => {
        if (tfaEnabled) {
            setConfirm({
                text: 'Disabling Two-Factor Authentication will remove an additional layer of security on your account. Are you sure you want to proceed?',
                textAction: 'Yes',
                clickAction: () => {
                    handleDisable2faOpe()
                        .then(({ title }) => {
                            setMessage({ type: 'success', text: title });
                            setConfirm(null);
                            authStateUpdated();
                        })
                        .catch((err) => {
                            setMessage({ type: 'error', text: err.toString() });
                        });
                }
            });
        } else {
            setModalType('twoFactorAuth');
        }
    };

    return (
        <div>
            <div className="max-w-[900px] mx-auto inner_page_cont">
                <div className="pb-3 mb-3 border-b border-primary-100">
                    <h2 className="text-3xl font-medium leading-normal">Login & Password</h2>
                </div>
                <InfoUpdateRow
                    icon={<LockFillIcon />}
                    title="Change Password"
                    subTitle={`Its a good idea to use a strong password that you're not using elsewhere`}
                    onClick={() => setModalType('changePassword')}
                />
                <InfoUpdateRow
                    icon={<ShieldFillIcon />}
                    title="Two-Factor Authentication"
                    subTitle={
                        tfaEnabled ? (
                            <span className="text-success">Enabled</span>
                        ) : (
                            <span className="text-error">Disabled</span>
                        )
                    }
                    action={<ToggleMenu onClick={handleToggleMenu} tfaEnabled={tfaEnabled} />}
                />
            </div>

            {modalType === 'twoFactorAuth' && (
                <LibModal title="Two Factor Authentication" setOpen={setModalType}>
                    <TwoFacAuthModal closeModal={() => setModalType('')} />
                </LibModal>
            )}

            {modalType === 'changePassword' && (
                <LibModal title="Change Password" setOpen={setModalType}>
                    <ChangePasswordModal closeModal={() => setModalType('')} />
                </LibModal>
            )}
        </div>
    );
}

export default SecurityPage;
